import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConversation } from '@11labs/react';
import { useHVAConversation } from './HVADynamicAgentConversationContext';
import { LOANS, getLoanTypeKey, FeedbackMessage } from './HVADynamicAgentData';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line
import { faMicrophone, faMicrophoneSlash, faPhoneSlash, faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import { getHVASignedUrl } from 'services/hvaagent.service';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

type HVAConversationWidgetProps = {
  agentId: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
};

const HVADynamicConversationWidget = ({
  agentId,
  onError,
  onFeedback
}: HVAConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);

  // Primero añadimos la función de limpieza de NIF
  const cleanNIF = (nif: string): string => {
    return nif.replace(/[-.\s]/g, '').toUpperCase();
  };

  // Obtenemos el contexto de la conversación
  // eslint-disable-next-line
  const { clientInfo, setIsNifValidated, setRequestTransfer, setRequestClose } = useHVAConversation();

  // Referencia para el contenedor de mensajes
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll hasta el último mensaje
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      setIsInitialized(false);
      setIsLoading(false);
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);
      setTimeout(scrollToBottom, 0);
    },
    // Añadimos la configuración de client tools
    clientTools: {
      requestAction: async (parameters: { requestedAction: string }) => {
        const { requestedAction } = parameters;

        switch (requestedAction) {
          case 'hangup': {
            setRequestClose(true);

            // Añadimos mensaje de feedback
            onFeedback({
              type: 'close',
              message: 'Solicitud de cuelgue',
              success: true
            });

            return JSON.stringify({
              success: true,
              message: `Adios`
            });
          }

          case 'transferToSupervisor': {
            setRequestTransfer(true);

            // Añadimos mensaje de feedback
            onFeedback({
              type: 'transfer',
              message: 'Solicitud de transferencia a supervisor',
              success: true
            });

            return JSON.stringify({
              success: true,
              message: `Transferencia solicitada`
            });
          }

          case 'transferToTPV': {
            setRequestTransfer(true);

            // Añadimos mensaje de feedback
            onFeedback({
              type: 'transfer',
              message: 'Solicitud de transferencia a TPV',
              success: true
            });

            return JSON.stringify({
              success: true,
              message: `Transferencia a TPV solicitada`
            });
          }

          default: {
            // Manejo de casos no implementados
            onFeedback({
              type: 'info',
              message: `Solicitud de acción no implementada: ${requestedAction}`,
              details: `Tipo de acción solicitada no disponible`,
              success: false
            });

            return JSON.stringify({
              success: false,
              data: null,
              message: `Tipo de acción solicitada '${requestedAction}' no implementada`
            });
          }
        }
      },

      requestInfo: async (parameters: { requestedInfo: string }) => {
        const { requestedInfo } = parameters;

        switch (requestedInfo) {
          case 'name': {
            // Retorna el nombre del cliente
            onFeedback({
              type: 'info',
              message: 'Solicitud de nombre del cliente',
              details: `Nombre: ${clientInfo?.name || 'No disponible'}`,
              success: !!clientInfo?.name
            });

            return JSON.stringify({
              success: true,
              data: `CustomerName: ${clientInfo?.name}`,
              message: ``
            });
          }

          case 'datetime': {
            // Retorna fecha y hora actuales
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('es-ES', options);

            onFeedback({
              type: 'info',
              message: 'Solicitud de fecha y hora',
              details: `Fecha y hora actual: ${dateTimeString}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'product': {
            try {
              if (!clientInfo?.loantype) {
                throw new Error('No hay tipo de préstamo asignado al cliente');
              }

              const loanType = getLoanTypeKey(clientInfo.loantype);
              const loanData = LOANS.prestamos[loanType];

              // Añadimos mensaje de feedback
              onFeedback({
                type: 'info',
                message: 'Solicitud de información de préstamo',
                details: `Información del préstamo tipo ${clientInfo.loantype} recuperada`,
                success: true
              });

              // Devolvemos los datos del préstamo
              return JSON.stringify({
                success: true,
                data: {
                  tipo: loanType,
                  importeMaximo: loanData.ImporteMaximo,
                  plazoMaximo: loanData.PlazoMaximo,
                  TIN: loanData.TIN,
                  variantes: loanData.Prestamos
                },
                message: ''
              });
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Error desconocido';

              onFeedback({
                type: 'info',
                message: 'Solicitud de información de préstamo',
                details: errorMessage,
                success: false
              });

              return JSON.stringify({
                success: false,
                data: null,
                message: errorMessage
              });
            }
          }

          default: {
            // Manejo de casos no implementados
            onFeedback({
              type: 'info',
              message: `Solicitud de información no implementada: ${requestedInfo}`,
              details: `Tipo de información solicitada no disponible`,
              success: false
            });

            return JSON.stringify({
              success: false,
              data: null,
              message: `Tipo de información solicitada '${requestedInfo}' no implementada`
            });
          }
        }
      },

      requestValidation: async (parameters: {
        paraValidar: string;
        valor: string;
      }) => {
        const { paraValidar, valor } = parameters;
        console.log('Validating:', paraValidar, valor);

        switch (paraValidar) {
          case 'NIF': {
            // Limpiamos el NIF recibido del agente
            const cleanedNIF = cleanNIF(valor);

            // Limpiamos el NIF del formulario para comparar
            const formNIF = clientInfo?.nif ? cleanNIF(clientInfo.nif) : '';

            // Validamos que coincida con el NIF del formulario
            const isValid = formNIF === cleanedNIF;
            setIsNifValidated(isValid);

            // Añadimos mensaje de feedback
            onFeedback({
              type: 'validation',
              message: 'Solicitud de verificación de NIF',
              // eslint-disable-next-line
              details: `NIF solicitado: ${valor} Resultado: ${isValid ? '✓ NIF válido' : '✗ NIF no coincide'}`,
              success: isValid
            });

            // devolvemos la información al agente
            return JSON.stringify({
              success: isValid,
              message: isValid ? 'NIF válido' : 'NIF no encontrado',
              validatedNif: isValid ? cleanedNIF : null
            });
          }

          case 'DATEOFBIRTH': {
            if (!valor || !clientInfo?.dateofbirth) {
              onFeedback({
                type: 'validation',
                message: 'Solicitud de verificación de fecha de nacimiento',
                details: 'Datos incompletos para la validación',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Datos incompletos para la validación',
                validatedDate: null
              });
            }

            try {
              // Función auxiliar para convertir Date a formato YYYYMMDD
              const dateToYYYYMMDD = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}${month}${day}`;
              };

              // Convertir la fecha del formulario a YYYYMMDD
              const formDateStr = dateToYYYYMMDD(clientInfo.dateofbirth);

              // La fecha proporcionada ya viene en formato YYYYMMDD, solo validamos su formato
              if (!/^\d{8}$/.test(valor)) {
                throw new Error('Formato de fecha inválido');
              }

              const isValid = formDateStr === valor;

              // Añadimos mensaje de feedback
              onFeedback({
                type: 'validation',
                message: 'Solicitud de verificación de fecha de nacimiento',
                // eslint-disable-next-line
                details: `Fecha solicitada: ${valor} Resultado: ${isValid ? '✓ Fecha válida' : '✗ Fecha no coincide'}`,
                success: isValid
              });

              return JSON.stringify({
                success: isValid,
                // eslint-disable-next-line
                message: isValid ? 'Fecha de nacimiento válida' : 'Fecha de nacimiento no coincide',
                validatedDate: isValid ? valor : null
              });
            } catch (error) {
              onFeedback({
                type: 'validation',
                message: 'Error en validación de fecha de nacimiento',
                details: 'Formato de fecha inválido',
                success: false
              });

              return JSON.stringify({
                success: false,
                message: 'Formato de fecha inválido',
                validatedDate: null
              });
            }
          }

          default: {
            // Manejo de casos no implementados
            onFeedback({
              type: 'validation',
              message: `Solicitud de validación no implementada: ${paraValidar}`,
              details: `Tipo de validación solicitada no disponible`,
              success: false
            });

            return JSON.stringify({
              success: false,
              data: null,
              message: `Tipo de validación solicitada '${paraValidar}' no implementada`
            });
          }
        }
      },

      reportCallStepResult: async (parameters: {
        callstep: string;
        stepresult: string;
      }) => {
        // Añadimos mensaje de feedback
        onFeedback({
          type: 'info',
          message: 'Fase de llamada completada',
          // eslint-disable-next-line
          details: `Fase: ${parameters.callstep} Resultado: ${parameters.stepresult}`,
          success: true
        });

        // devolvemos la información al agente
        return JSON.stringify({
          success: 'true'
        });
      }
    }
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);

      // Obtener URL firmada
      const signedUrl = await getHVASignedUrl(agentId);

      // Iniciar sesión con URL firmada
      setMessages([]);
      await conversation.startSession({ signedUrl });
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [agentId, conversation, hasPermission, onError]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Efecto para inicializar el scroll cuando se montan los mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Efecto para acceder al micrófono
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-0">
      <Row className="mb-2 mt-2">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-center">
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Status:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-3 pt-2 pb-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm shadow-sm"
        style={{
          height: '456px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.length > 0 && (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-2 ${
                  msg.source === 'user' ? 'bg-body' : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {!hasPermission && (
        <div className="fs-9 p-2 bg-body rounded-lg mt-2">
          Microphone permission is required for conversation
        </div>
      )}
    </div>
  );
};

export default HVADynamicConversationWidget;
